/**
 * Klaviyo New Size Request Web Component
 *
 * Displays a form when an unavailable (non-existent) variant combination is selected,
 * allowing customers to request that this size/color be made available.
 * Uses Klaviyo's Create Client Event API to track a custom "Requested New Size" event.
 * 
 * https://developers.klaviyo.com/en/reference/events_api_overview
 *
 * Listens to: variantAvailabilityChange event on [data-section-type="product"]
 */
class KlaviyoNewSize extends HTMLElement {
  constructor() {
    super();

    this.productContainer = null;
    this.selectedOptions = null;

    // Bind methods
    this.handleVariantChange = this.handleVariantChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleKeypress = this.handleKeypress.bind(this);
  }

  /**
   * Called when the element is added to the DOM
   */
  connectedCallback() {
    this.productContainer = document.querySelector('[data-section-type="product"]');

    this.cacheElements();
    this.bindEvents();

    // Hide by default - will show when unavailable variant is selected
    this.hide();
  }

  /**
   * Called when the element is removed from the DOM
   */
  disconnectedCallback() {
    this.unbindEvents();
  }

  /**
   * Cache DOM element references
   */
  cacheElements() {
    this.elements = {
      emailInput: this.querySelector("[data-newsize-email]"),
      submitButton: this.querySelector("[data-newsize-submit]"),
      errorMessage: this.querySelector("[data-newsize-error]"),
      successMessage: this.querySelector("[data-newsize-success]"),
      formWrapper: this.querySelector("[data-newsize-form]"),
      optionsInput: this.querySelector("[data-newsize-options]"),
    };
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    if (this.productContainer) {
      this.productContainer.addEventListener(
        "variantAvailabilityChange",
        this.handleVariantChange
      );
    }

    if (this.elements.submitButton) {
      this.elements.submitButton.addEventListener("click", this.handleSubmit);
    }

    if (this.elements.emailInput) {
      this.elements.emailInput.addEventListener("keypress", this.handleKeypress);
    }
  }

  /**
   * Unbind event listeners
   */
  unbindEvents() {
    if (this.productContainer) {
      this.productContainer.removeEventListener(
        "variantAvailabilityChange",
        this.handleVariantChange
      );
    }

    if (this.elements.submitButton) {
      this.elements.submitButton.removeEventListener("click", this.handleSubmit);
    }

    if (this.elements.emailInput) {
      this.elements.emailInput.removeEventListener("keypress", this.handleKeypress);
    }
  }

  /**
   * Get the Klaviyo company ID
   */
  get companyId() {
    return this.getAttribute("company-id");
  }

  /**
   * Get the product title
   */
  get productTitle() {
    return this.getAttribute("product-title") || "";
  }

  /**
   * Get the product URL
   */
  get productUrl() {
    return this.getAttribute("product-url") || window.location.href;
  }

  /**
   * Get the product ID
   */
  get productId() {
    return this.getAttribute("product-id") || "";
  }

  /**
   * Handle variant availability change event
   * @param {CustomEvent} event
   */
  handleVariantChange(event) {
    const { isUnavailable, selectedOptions } = event.detail;

    if (isUnavailable) {
      this.selectedOptions = selectedOptions || this.getSelectedOptionsFromDOM();
      this.updateOptionsDisplay();
      this.show();
    } else {
      this.hide();
    }
  }

  /**
   * Get selected options from the DOM as a fallback
   * @returns {Object}
   */
  getSelectedOptionsFromDOM() {
    const options = {};
    const selectors = this.productContainer?.querySelectorAll(
      "[data-option-index] input:checked, [data-option-index] select"
    );

    selectors?.forEach((selector) => {
      const name =
        selector.closest("[data-option-index]")?.dataset.optionName ||
        selector.name ||
        "Option";
      const value = selector.value;
      if (value) {
        options[name] = value;
      }
    });

    return options;
  }

  /**
   * Update the hidden options input with selected options
   */
  updateOptionsDisplay() {
    if (this.elements.optionsInput && this.selectedOptions) {
      const optionsText = Object.entries(this.selectedOptions)
        .map(([key, value]) => `${key}: ${value}`)
        .join(", ");
      this.elements.optionsInput.value = optionsText || "Selected combination";
    }
  }

  /**
   * Handle keypress on email input (submit on Enter)
   * @param {KeyboardEvent} event
   */
  handleKeypress(event) {
    if (event.key === "Enter") {
      event.preventDefault();
      this.handleSubmit();
    }
  }

  /**
   * Show the component
   */
  show() {
    this.style.display = "block";
    this.resetForm();
  }

  /**
   * Hide the component
   */
  hide() {
    this.style.display = "none";
    this.resetForm();
  }

  /**
   * Reset the form to its initial state
   */
  resetForm() {
    const { emailInput, errorMessage, successMessage, formWrapper, submitButton } =
      this.elements;

    if (emailInput) emailInput.value = "";

    if (errorMessage) {
      errorMessage.style.display = "none";
      errorMessage.textContent = "";
    }

    if (successMessage) successMessage.style.display = "none";
    if (formWrapper) formWrapper.style.display = "flex";

    if (submitButton) {
      submitButton.disabled = false;
      const span = submitButton.querySelector("span");
      if (span) {
        span.textContent = submitButton.dataset.originalText || "Submit";
      }
    }
  }

  /**
   * Show an error message
   * @param {string} message
   */
  showError(message) {
    const { errorMessage, submitButton } = this.elements;

    if (errorMessage) {
      errorMessage.textContent = message;
      errorMessage.style.display = "block";
    }

    if (submitButton) {
      submitButton.disabled = false;
      const span = submitButton.querySelector("span");
      if (span) {
        span.textContent = submitButton.dataset.originalText || "Submit";
      }
    }
  }

  /**
   * Show the success message and hide the form
   */
  showSuccess() {
    const { formWrapper, errorMessage, successMessage } = this.elements;

    if (formWrapper) formWrapper.style.display = "none";
    if (errorMessage) errorMessage.style.display = "none";
    if (successMessage) successMessage.style.display = "block";
  }

  /**
   * Set loading state on the submit button
   * @param {boolean} isLoading
   */
  setLoading(isLoading) {
    const { submitButton, errorMessage } = this.elements;

    if (submitButton) {
      submitButton.disabled = isLoading;
      const span = submitButton.querySelector("span");
      if (span) {
        if (isLoading) {
          submitButton.dataset.originalText = span.textContent;
          span.textContent = "Submitting...";
        } else {
          span.textContent = submitButton.dataset.originalText || "Submit";
        }
      }
    }

    if (isLoading && errorMessage) {
      errorMessage.style.display = "none";
    }
  }

  /**
   * Validate email format
   * @param {string} email
   * @returns {boolean}
   */
  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Handle form submission
   */
  async handleSubmit() {
    const email = this.elements.emailInput?.value.trim() || "";

    // Validate email
    if (!email) {
      this.showError("Please enter your email address.");
      return;
    }

    if (!this.isValidEmail(email)) {
      this.showError("Please enter a valid email address.");
      return;
    }

    this.setLoading(true);

    try {
      await this.submitNewSizeRequest(email);
      this.showSuccess();
    } catch (error) {
      this.showError(error.message);
    }
  }

  /**
   * Submit new size request to Klaviyo using the Create Client Event API
   * @param {string} email
   * @returns {Promise<boolean>}
   */
  async submitNewSizeRequest(email) {
    // Build the selected options string
    const optionsString = this.selectedOptions
      ? Object.entries(this.selectedOptions)
          .map(([key, value]) => `${key}: ${value}`)
          .join(", ")
      : "Unknown combination";

    const payload = {
      data: {
        type: "event",
        attributes: {
          metric: {
            data: {
              type: "metric",
              attributes: {
                name: "Requested New Size",
              },
            },
          },
          profile: {
            data: {
              type: "profile",
              attributes: {
                email: email,
              },
            },
          },
          properties: {
            ProductName: this.productTitle,
            ProductID: this.productId,
            ProductURL: this.productUrl,
            RequestedOptions: optionsString,
            ...this.selectedOptions,
          },
        },
      },
    };

    const response = await fetch(
      `https://a.klaviyo.com/client/events/?company_id=${this.companyId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          revision: "2024-06-15",
        },
        body: JSON.stringify(payload),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorDetail =
        errorData.errors?.[0]?.detail || "Something went wrong. Please try again.";
      throw new Error(errorDetail);
    }

    return true;
  }
}

// Register the custom element
if (!customElements.get("klaviyo-new-size")) {
  customElements.define("klaviyo-new-size", KlaviyoNewSize);
}
